import { PickAnAttribute } from '../../modules/PickAnAttribute.js';
import { KatapultJob } from '../../modules/katapult-job/KatapultJob.js';
import { DESIGN_CATEGORY_ATTRIBUTE, REMOVED_FROM_SCOPE_ATTRIBUTE, resolveNodeBreakAngle } from './NodeBreakAngle.js';

/**
 * Computes and writes the Break Angle node attribute, using KatapultJob (constructed from the injected Firebase
 * instance) for all job data access.
 */
export class BreakAngleAttributeService {
  /**
   * @param {object} firebaseApp - The Firebase app instance to read/write job data with.
   * @param {string} jobId - The id of the job to operate on.
   */
  constructor(firebaseApp, jobId) {
    this.job = new KatapultJob(jobId, { firebaseApp });
  }

  /**
   * Resolves the node's break angle from its connections and, depending on the outcome, writes its `break_angle`
   * attribute:
   * - Two or more active connections: overwrites `break_angle` with the lowest break angle between any pair.
   * - Exactly one active connection: if the node doesn't have a non-blank `break_angle` value yet, adds a blank one
   *   for a person to fill in manually (there's no automatic way to pick the right second connection to compare
   *   against) unless a blank one is already there from an earlier run. If it already has a real value, leaves the
   *   node untouched.
   * - No active connections: leaves the node untouched.
   * @param {string} nodeId - The id of the node to evaluate.
   * @param {string} connectionTypeAttribute - The attribute key that stores a connection's type (e.g. `connection_type`).
   * @returns {Promise<{status: 'resolved'|'needsManualReview'|'alreadyReviewed'|'skip', breakAngle: ?number}>} What happened, and the
   * value written (if any).
   * @throws {Error} If the node can't be found, or if reading/writing job data fails.
   */
  async applyToNode(nodeId, connectionTypeAttribute) {
    const node = await this.job.getNodeById(nodeId);
    if (node == null) throw new Error(`Node not found: ${nodeId}`);

    const connections = await this.job.getNodeConnections(nodeId);
    const connectionCandidates = (
      await Promise.all(
        Object.entries(connections).map(async ([connectionId, connection]) => {
          const otherNodeId = connection.node_id_1 === nodeId ? connection.node_id_2 : connection.node_id_1;
          const otherNode = await this.job.getNodeById(otherNodeId);
          // A connection whose other endpoint can't be found has no coordinates to compare against.
          if (otherNode == null) return null;
          return {
            id: connectionId,
            type: PickAnAttribute(connection.attributes, connectionTypeAttribute),
            designCategory: PickAnAttribute(connection.attributes, DESIGN_CATEGORY_ATTRIBUTE),
            removedFromScope: PickAnAttribute(connection.attributes, REMOVED_FROM_SCOPE_ATTRIBUTE) === true,
            otherLatLng: { lat: otherNode.latitude, lng: otherNode.longitude }
          };
        })
      )
    ).filter((candidate) => candidate != null);

    const resolution = resolveNodeBreakAngle({ lat: node.latitude, lng: node.longitude }, connectionCandidates);

    if (resolution.status === 'resolved') {
      await this.job.updateNodeAttribute(nodeId, 'break_angle', { app_added: resolution.breakAngle }, { replaceExisting: true });
      return { status: 'resolved', breakAngle: resolution.breakAngle ?? null };
    }

    if (resolution.status === 'needsManualReview') {
      const existingBreakAngle = PickAnAttribute(node.attributes, 'break_angle');
      if (existingBreakAngle != null && existingBreakAngle !== '') {
        return { status: 'alreadyReviewed', breakAngle: null };
      }
      // Only add a blank placeholder if the attribute doesn't exist yet - if it's already there but blank (from an
      // earlier run the user hasn't filled in), leave it as-is rather than adding a second blank instance.
      if (node.attributes?.break_angle == null) {
        await this.job.addNodeAttributeValue(nodeId, 'break_angle', '');
      }
      return { status: 'needsManualReview', breakAngle: null };
    }

    return { status: 'skip', breakAngle: null };
  }
}
